/* eslint-disable */
/**
 * Phase 98o — Mini-CMS Photos Boutique.
 *
 * Permet à l'admin de :
 * - Voir toutes les photos boutique (topbar + 4 étapes savoir-faire)
 * - Uploader une nouvelle image pour chaque slug (drag/drop ou input file)
 * - Modifier le caption / l'ordre / l'état enabled
 * - Prévisualiser en temps réel
 * - Phase 98p — Créer un nouveau slot photo libre (ex: logo-shop, hero-boutique)
 */
import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Image as ImageIcon, Upload, Save, Loader2, RefreshCw, Trash2, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Convertit un message d'erreur Axios en string sûre pour toast.error / React.
 * Notamment : les erreurs Pydantic 422 renvoient `detail` sous forme d'array
 * [{type, loc, msg, input, ctx, url}] — les passer à un composant React
 * provoque le crash « Objects are not valid as a React child » et une page blanche.
 */
function formatApiError(err, fallback = "Erreur inconnue") {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" · ");
  }
  if (typeof detail === "object") return JSON.stringify(detail);
  return String(detail);
}

const PLACEMENT_META = {
  topbar: { label: "Bandeau haut de page", color: "bg-purple-100 text-purple-800 border-purple-300" },
  savoir_faire: { label: "Section « Savoir-faire »", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  global: { label: "Global (logo, hero…)", color: "bg-amber-100 text-amber-800 border-amber-300" },
  hote_distributeur: { label: "Landing « Devenir hôte distributeur »", color: "bg-sky-100 text-sky-800 border-sky-300" },
};

export default function ShopPhotosCms() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({});  // slug → true si caption modifié
  const [uploadingSlug, setUploadingSlug] = useState("");
  const [showNewDialog, setShowNewDialog] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shop/boutique-section");
      setPhotos(data.photos || []);
      setDirty({});
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (slug, field, value) => {
    setPhotos((prev) => prev.map((p) => (p.slug === slug ? { ...p, [field]: value } : p)));
    setDirty((d) => ({ ...d, [slug]: true }));
  };

  const savePhotoMeta = async (slug) => {
    const p = photos.find((x) => x.slug === slug);
    if (!p) return;
    try {
      await api.put(`/admin/shop/boutique-section/${slug}`, {
        caption: p.caption || "",
        enabled: Boolean(p.enabled),
        sort_order: Number(p.sort_order || 0),
        placement: p.placement || "savoir_faire",
      });
      toast.success(`Métadonnées enregistrées pour "${slug}"`);
      setDirty((d) => ({ ...d, [slug]: false }));
    } catch (e) {
      toast.error(formatApiError(e, "Erreur enregistrement"));
    }
  };

  const uploadFile = async (slug, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Fichier trop lourd (max 15 MB)");
      return;
    }
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/shop/boutique-section/${slug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Image mise à jour pour "${slug}"`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur upload"));
    } finally {
      setUploadingSlug("");
    }
  };

  const deletePhoto = async (slug) => {
    const ok = await confirm({
      title: `Supprimer la photo "${slug}" ?`,
      description: "Cette image ne s'affichera plus sur la boutique. Vous pourrez la ré-uploader ensuite.",
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/shop/boutique-section/${slug}`);
      toast.success(`Photo "${slug}" supprimée`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur suppression"));
    }
  };

  const reseedFromSources = async () => {
    const ok = await confirm({
      title: "Recharger toutes les photos depuis les sources par défaut ?",
      description: "Cela écrase toutes les photos actuelles avec les images sources par défaut du code. Utile si des slugs affichent la mauvaise image.",
      confirmText: "Recharger tout",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/shop/boutique-section/reseed?force=true");
      toast.success(`${data.reseeded?.length || 0} photos rechargées depuis les sources`);
      await load();
    } catch (e) {
      toast.error("Erreur reseed");
    }
  };

  const syncCaptionsFromSeed = async () => {
    const ok = await confirm({
      title: "Réinitialiser les légendes par défaut ?",
      description:
        "Remplace les captions actuels des 4 étapes « savoir-faire » (et du bandeau) par les textes du seed. Utile si des captions sont dupliqués « en couple ». Les images ne sont pas modifiées.",
      confirmText: "Réinitialiser les légendes",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/shop/boutique-section/sync-captions?force=true");
      const n = data.count_updated || 0;
      if (n === 0) {
        toast.info("Aucun caption à réinitialiser (tout est déjà à jour).");
      } else {
        toast.success(`${n} caption${n > 1 ? "s" : ""} réinitialisé${n > 1 ? "s" : ""} au texte par défaut`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur sync captions"));
    }
  };

  const dedupeSlugs = async () => {
    // Étape 1 — dry-run pour montrer combien de doublons seront supprimés
    let plan;
    try {
      const { data } = await api.post("/admin/shop/boutique-section/dedupe?dry_run=true");
      plan = data;
    } catch (e) {
      toast.error(formatApiError(e, "Erreur analyse doublons"));
      return;
    }
    if (!plan.duplicate_slugs_count) {
      toast.success("Aucun doublon détecté. Base propre.");
      return;
    }
    const details = plan.duplicates
      .map((d) => `• ${d.slug} → conserve ${d.kept.size || 0} KB, supprime ${d.removed.length} doublon${d.removed.length > 1 ? "s" : ""}`)
      .join("\n");
    const ok = await confirm({
      title: `${plan.planned_deletions} document${plan.planned_deletions > 1 ? "s" : ""} en doublon à supprimer`,
      description: `Ces doublons expliquent l'affichage "en couple" de captions/photos. Le plus lourd (ou le plus récent) est conservé pour chaque slug.\n\n${details}`,
      confirmText: "Nettoyer maintenant",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/shop/boutique-section/dedupe?dry_run=false");
      toast.success(`${data.deleted_count} doublon${data.deleted_count > 1 ? "s" : ""} supprimé${data.deleted_count > 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur dedupe"));
    }
  };

  const savoirItems = photos.filter((p) => p.placement === "savoir_faire").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const topbarItems = photos.filter((p) => p.placement === "topbar");
  const globalItems = photos.filter((p) => p.placement === "global");
  const hostItems = photos.filter((p) => p.placement === "hote_distributeur");
  const otherItems = photos.filter((p) => !["savoir_faire", "topbar", "global", "hote_distributeur"].includes(p.placement));

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="shop-photos-cms-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-[#1A1C1A]">Photos boutique</h1>
            <p className="text-sm text-[#6B6E6B]">
              Uploadez vous-même les images du bandeau et des 4 étapes savoir-faire.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={dedupeSlugs}
            data-testid="dedupe-btn"
            className="gap-2 border-[#C05C47] text-[#C05C47] hover:bg-[#C05C47]/10"
          >
            <Trash2 className="w-4 h-4" /> Nettoyer les doublons
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={syncCaptionsFromSeed}
            data-testid="sync-captions-btn"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Réinitialiser les légendes
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reseedFromSources}
            data-testid="reseed-btn"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Recharger depuis les sources
          </Button>
          <Button
            size="sm"
            onClick={() => setShowNewDialog(true)}
            data-testid="new-slot-btn"
            className="gap-2 bg-[#1F4E3D] hover:bg-[#173D2F]"
          >
            <Plus className="w-4 h-4" /> Ajouter un slot photo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#1F4E3D]" />
        </div>
      ) : (
        <>
          {/* Info doublons — placé en HAUT pour être visible immédiatement */}
          <DupCheckWarning photos={photos} />

          {/* Section bandeau */}
          {topbarItems.length > 0 && (
            <Section title="Bandeau permanent (haut de page)" placement="topbar">
              <div className="grid grid-cols-1 gap-3">
                {topbarItems.map((p) => (
                  <PhotoCard
                    key={p.id || p.slug}
                    photo={p}
                    isDirty={dirty[p.slug]}
                    uploading={uploadingSlug === p.slug}
                    onUpload={(f) => uploadFile(p.slug, f)}
                    onChange={(field, value) => updateField(p.slug, field, value)}
                    onSave={() => savePhotoMeta(p.slug)}
                    onDelete={() => deletePhoto(p.slug)}
                    imgAspect="aspect-[4/1]"
                  />
                ))}
              </div>
            </Section>
          )}

          {/* Section savoir-faire */}
          <Section title={`Section « Notre savoir-faire » (${savoirItems.length} étapes)`} placement="savoir_faire">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {savoirItems.map((p) => (
                <PhotoCard
                  key={p.id || p.slug}
                  photo={p}
                  isDirty={dirty[p.slug]}
                  uploading={uploadingSlug === p.slug}
                  onUpload={(f) => uploadFile(p.slug, f)}
                  onChange={(field, value) => updateField(p.slug, field, value)}
                  onSave={() => savePhotoMeta(p.slug)}
                  onDelete={() => deletePhoto(p.slug)}
                  imgAspect="aspect-[4/5]"
                />
              ))}
            </div>
          </Section>

          {/* Section landing « Devenir hôte distributeur » (Phase 100) */}
          {hostItems.length > 0 && (
            <Section title={`Landing « Devenir hôte distributeur » (${hostItems.length} photo${hostItems.length > 1 ? "s" : ""})`} placement="hote_distributeur">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {hostItems.map((p) => (
                  <PhotoCard
                    key={p.id || p.slug}
                    photo={p}
                    isDirty={dirty[p.slug]}
                    uploading={uploadingSlug === p.slug}
                    onUpload={(f) => uploadFile(p.slug, f)}
                    onChange={(field, value) => updateField(p.slug, field, value)}
                    onSave={() => savePhotoMeta(p.slug)}
                    onDelete={() => deletePhoto(p.slug)}
                    imgAspect="aspect-[4/5]"
                  />
                ))}
              </div>
            </Section>
          )}

          {/* Section global (logo, hero, autres assets) */}
          {globalItems.length > 0 && (
            <Section title={`Assets globaux · logo, hero (${globalItems.length})`} placement="global">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {globalItems.map((p) => (
                  <PhotoCard
                    key={p.id || p.slug}
                    photo={p}
                    isDirty={dirty[p.slug]}
                    uploading={uploadingSlug === p.slug}
                    onUpload={(f) => uploadFile(p.slug, f)}
                    onChange={(field, value) => updateField(p.slug, field, value)}
                    onSave={() => savePhotoMeta(p.slug)}
                    onDelete={() => deletePhoto(p.slug)}
                    imgAspect="aspect-[16/9]"
                  />
                ))}
              </div>
            </Section>
          )}

          {otherItems.length > 0 && (
            <Section title="Autres photos" placement="other">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {otherItems.map((p) => (
                  <PhotoCard
                    key={p.id || p.slug}
                    photo={p}
                    isDirty={dirty[p.slug]}
                    uploading={uploadingSlug === p.slug}
                    onUpload={(f) => uploadFile(p.slug, f)}
                    onChange={(field, value) => updateField(p.slug, field, value)}
                    onSave={() => savePhotoMeta(p.slug)}
                    onDelete={() => deletePhoto(p.slug)}
                    imgAspect="aspect-[4/5]"
                  />
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {/* Dialog création nouveau slot */}
      <NewSlotDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={() => { setShowNewDialog(false); load(); }}
      />
    </div>
  );
}


function Section({ title, placement, children }) {
  const meta = PLACEMENT_META[placement];
  return (
    <section className="border border-[#E5E5E0] bg-white rounded-md overflow-hidden">
      <div className="px-4 py-3 bg-[#F5F5F0] border-b border-[#E5E5E0] flex items-center gap-2">
        <h2 className="text-sm font-semibold text-[#1A1C1A]">{title}</h2>
        {meta && <Badge variant="secondary" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>}
      </div>
      <div className="p-4">
        {children}
      </div>
    </section>
  );
}


function PhotoCard({ photo, isDirty, uploading, onUpload, onChange, onSave, onDelete, imgAspect = "aspect-[4/5]" }) {
  const fileRef = useRef(null);
  const cacheBust = photo.uploaded_at ? `?v=${encodeURIComponent(photo.uploaded_at)}` : "";
  const src = `${API_URL}/api/shop/boutique-section/${photo.slug}/photo${cacheBust}`;

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) onUpload(f);
  };

  return (
    <div className="border border-[#E5E5E0] rounded-md overflow-hidden bg-white flex flex-col" data-testid={`photo-card-${photo.slug}`}>
      {/* Preview + drop zone */}
      <div
        className={`relative w-full ${imgAspect} bg-[#1A1C1A] flex items-center justify-center overflow-hidden`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <img
          src={src}
          alt={photo.caption || photo.slug}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }}
          data-testid={`photo-preview-${photo.slug}`}
        />
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
            <span className="text-white ml-2 text-sm">Upload…</span>
          </div>
        )}
        <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-[10px] font-mono rounded">
          {photo.slug}
        </div>
        {!photo.enabled && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-[10px] rounded">
            désactivée
          </div>
        )}
      </div>

      {/* Metadata + actions */}
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Caption (visible boutique)</label>
          <Textarea
            value={photo.caption || ""}
            onChange={(e) => onChange("caption", e.target.value)}
            rows={2}
            className="text-sm"
            data-testid={`caption-${photo.slug}`}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Ordre</label>
            <Input
              type="number"
              value={photo.sort_order || 0}
              onChange={(e) => onChange("sort_order", Number(e.target.value))}
              className="h-9 font-mono"
              data-testid={`sort-${photo.slug}`}
            />
          </div>
          <div className="flex items-end pb-1 gap-2">
            <Switch
              checked={Boolean(photo.enabled)}
              onCheckedChange={(v) => onChange("enabled", v)}
              data-testid={`enabled-${photo.slug}`}
            />
            <span className="text-sm">Activée</span>
          </div>
        </div>
        <div className="text-[10px] text-[#6B6E6B] font-mono">
          {photo.size ? `${Math.round(photo.size / 1024)} KB` : ""} · MAJ {(photo.uploaded_at || "").slice(0, 10)}
        </div>
        <div className="flex gap-2 mt-auto pt-2">
          <input
            type="file"
            accept="image/*"
            ref={fileRef}
            onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
            className="hidden"
            data-testid={`file-input-${photo.slug}`}
          />
          <Button
            size="sm"
            variant="outline"
            className="flex-1 gap-1"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-btn-${photo.slug}`}
          >
            <Upload className="w-3 h-3" /> Remplacer
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!isDirty}
            className={isDirty ? "bg-[#1F4E3D] hover:bg-[#173D2F]" : ""}
            data-testid={`save-btn-${photo.slug}`}
          >
            <Save className="w-3 h-3 mr-1" /> {isDirty ? "Enregistrer" : "OK"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="text-[#C05C47] hover:bg-[#C05C47]/10"
            data-testid={`delete-btn-${photo.slug}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function DupCheckWarning({ photos }) {
  // Détecte les slugs présents plusieurs fois (bug 98v : doublons en base)
  // et les slugs qui ont exactement la même taille de blob (probable doublon d'image)
  const bySlug = {};
  const bySize = {};
  for (const p of photos) {
    bySlug[p.slug] = (bySlug[p.slug] || 0) + 1;
    if (!p.size) continue;
    if (!bySize[p.size]) bySize[p.size] = [];
    bySize[p.size].push(p.slug);
  }
  const slugDups = Object.entries(bySlug).filter(([, n]) => n > 1);
  const sizeDups = Object.entries(bySize).filter(([, slugs]) => slugs.length > 1);
  if (!slugDups.length && !sizeDups.length) return null;
  return (
    <div className="space-y-2">
      {slugDups.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-300 rounded text-sm flex gap-2 items-start" data-testid="slug-dup-warning">
          <AlertCircle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
          <div>
            <strong className="text-red-900">Doublons de slug détectés en base ({slugDups.length})</strong>
            <div className="text-xs text-red-800 mt-1">
              Ces slugs existent en plusieurs exemplaires — c'est ce qui affiche les captions/photos « en couple » sur la boutique :
              {slugDups.map(([slug, n]) => (
                <div key={slug} className="font-mono mt-0.5">
                  → <strong>{slug}</strong> × {n}
                </div>
              ))}
              Cliquez sur <strong>« Nettoyer les doublons »</strong> en haut de page pour corriger.
            </div>
          </div>
        </div>
      )}
      {sizeDups.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm flex gap-2 items-start" data-testid="dup-warning">
          <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <strong className="text-amber-900">Photos potentiellement dupliquées</strong>
            <div className="text-xs text-amber-800 mt-1">
              Ces slugs ont exactement la même taille de blob → probablement la même image :
              {sizeDups.map(([sz, slugs]) => (
                <div key={sz} className="font-mono mt-0.5">
                  → {slugs.join(" = ")} ({Math.round(Number(sz) / 1024)} KB)
                </div>
              ))}
              Uploadez une image différente pour l'un des slugs pour corriger.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============== Phase 98p — Dialog création nouveau slot photo ==============

/** Slugs réservés avec usage prédéfini côté boutique */
const RESERVED_SLUGS = [
  { slug: "logo-shop", label: "Logo boutique (footer/header)", placement: "global" },
  { slug: "hero-boutique", label: "Grande image d'ouverture (hero)", placement: "global" },
  { slug: "distributeur-photo", label: "Photo distributeur — Landing hôte", placement: "hote_distributeur" },
];

function NewSlotDialog({ open, onClose, onCreated }) {
  const [slug, setSlug] = useState("");
  const [placement, setPlacement] = useState("savoir_faire");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setSlug("");
    setPlacement("savoir_faire");
    setCaption("");
    setFile(null);
  };

  const submit = async () => {
    const cleanSlug = (slug || "").trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    if (!cleanSlug) {
      toast.error("Le slug est requis");
      return;
    }
    if (!file) {
      toast.error("Un fichier image est requis");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caption) fd.append("caption", caption);
      await api.post(`/admin/shop/boutique-section/${cleanSlug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Puis PUT metadata pour fixer placement + caption
      await api.put(`/admin/shop/boutique-section/${cleanSlug}`, {
        placement,
        caption: caption || cleanSlug,
        sort_order: 99,
        enabled: true,
      });
      toast.success(`Slot "${cleanSlug}" créé`);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur création"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-lg" data-testid="new-slot-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau slot photo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Slugs réservés — click pour pré-remplir */}
          <div>
            <Label className="text-xs">Suggestions rapides</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {RESERVED_SLUGS.map((r) => (
                <Button
                  key={r.slug}
                  variant="outline"
                  size="sm"
                  className="text-[10px] font-mono h-7"
                  onClick={() => { setSlug(r.slug); setPlacement(r.placement); setCaption(r.label); }}
                  data-testid={`preset-${r.slug}`}
                >
                  {r.slug}
                </Button>
              ))}
            </div>
            <p className="text-[10px] text-[#6B6E6B] mt-1">
              <strong>logo-shop</strong> = affiché dans le footer (au-dessus du titre). <strong>hero-boutique</strong> = future image d'accueil.
            </p>
          </div>
          <div>
            <Label className="text-xs">Slug (identifiant unique, minuscules + tirets)</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="ex: logo-shop, hero-produit, banniere-noel..."
              className="font-mono"
              data-testid="new-slug-input"
            />
          </div>
          <div>
            <Label className="text-xs">Placement</Label>
            <select
              className="w-full border border-[#E5E5E0] rounded-md h-9 px-2 text-sm"
              value={placement}
              onChange={(e) => setPlacement(e.target.value)}
              data-testid="new-placement-select"
            >
              <option value="savoir_faire">Section savoir-faire</option>
              <option value="topbar">Bandeau haut de page</option>
              <option value="global">Global (logo, hero, autres)</option>
              <option value="hote_distributeur">Landing « Devenir hôte distributeur »</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Caption</Label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Texte descriptif (visible sur la boutique)"
              data-testid="new-caption-input"
            />
          </div>
          <div>
            <Label className="text-xs">Fichier image (max 15 MB)</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full border border-[#E5E5E0] rounded-md p-2 text-sm"
              data-testid="new-file-input"
            />
            {file && (
              <p className="text-[10px] text-[#6B6E6B] mt-1">
                Sélectionné : <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }} data-testid="new-slot-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={uploading || !slug || !file}
            className="bg-[#1F4E3D] hover:bg-[#173D2F]"
            data-testid="new-slot-submit"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

